/**
 * Emoji & Content Animation Mapper
 * Detects emojis and reaction-worthy content in chat messages,
 * then triggers corresponding morph-target animations on the avatar.
 */

import { resolveMorphTargetIndex } from '../converter';

export interface EmojiAnimation {
  morphTargets: Record<string, number>;
  duration: number;
  easeIn: number;
  easeOut: number;
}

const EMOJI_ANIMATIONS: Record<string, EmojiAnimation> = {
  smile: {
    morphTargets: {
      mouthSmileLeft: 0.42,
      mouthSmileRight: 0.42,
      cheekSquintLeft: 0.20,
      cheekSquintRight: 0.20,
      eyeSquintLeft: 0.10,
      eyeSquintRight: 0.10,
    },
    duration: 4600,
    easeIn: 300,
    easeOut: 3200,
  },
  laugh: {
    morphTargets: {
      mouthSmileLeft: 0.58,
      mouthSmileRight: 0.58,
      jawOpen: 0.28,
      cheekSquintLeft: 0.28,
      cheekSquintRight: 0.28,
      eyeSquintLeft: 0.22,
      eyeSquintRight: 0.22,
      browInnerUp: 0.15,
    },
    duration: 5200,
    easeIn: 200,
    easeOut: 3200,
  },
  wink: {
    morphTargets: {
      eyeBlinkLeft: 0.0,
      eyeBlinkRight: 0.9,
      mouthSmileLeft: 0.3,
      mouthSmileRight: 0.4,
    },
    duration: 800,
    easeIn: 150,
    easeOut: 200,
  },
  sad: {
    morphTargets: {
      mouthFrownLeft: 0.5,
      mouthFrownRight: 0.5,
      browInnerUp: 0.4,
      browDownLeft: 0.2,
      browDownRight: 0.2,
    },
    duration: 2000,
    easeIn: 400,
    easeOut: 600,
  },
  surprise: {
    morphTargets: {
      eyeWideLeft: 0.6,
      eyeWideRight: 0.6,
      browOuterUpLeft: 0.5,
      browOuterUpRight: 0.5,
      browInnerUp: 0.4,
      jawOpen: 0.3,
      mouthFunnel: 0.2,
    },
    duration: 1200,
    easeIn: 100,
    easeOut: 400,
  },
  thinking: {
    morphTargets: {
      browDownLeft: 0.3,
      browInnerUp: 0.2,
      eyeSquintLeft: 0.15,
      mouthPressLeft: 0.2,
      mouthPressRight: 0.15,
    },
    duration: 2000,
    easeIn: 400,
    easeOut: 500,
  },
};

const EMOJI_TO_ANIMATION: Record<string, keyof typeof EMOJI_ANIMATIONS> = {
  '\u{1F60A}': 'smile', // 😊
  '\u{1F603}': 'smile', // 😃
  '\u{1F604}': 'smile', // 😄
  '\u{1F600}': 'smile', // 😀
  '\u{263A}': 'smile',  // ☺
  '\u{1F602}': 'laugh', // 😂
  '\u{1F923}': 'laugh', // 🤣
  '\u{1F606}': 'laugh', // 😆
  '\u{1F609}': 'wink',  // 😉
  '\u{1F622}': 'sad',   // 😢
  '\u{1F625}': 'sad',   // 😥
  '\u{1F62D}': 'sad',   // 😭
  '\u{1F632}': 'surprise', // 😲
  '\u{1F62E}': 'surprise', // 😮
  '\u{1F914}': 'thinking', // 🤔
  ':)': 'smile',
  ':-)': 'smile',
  ':D': 'laugh',
  ':-D': 'laugh',
  ';)': 'wink',
  ';-)': 'wink',
  ':(': 'sad',
  ':-(': 'sad',
  ':O': 'surprise',
  ':-O': 'surprise',
};

// Text patterns that trigger animations
const TEXT_TRIGGERS: Array<{ pattern: RegExp; animation: keyof typeof EMOJI_ANIMATIONS }> = [
  { pattern: /\bha+h?a+\b/i, animation: 'laugh' },
  { pattern: /\blol\b/i, animation: 'laugh' },
  { pattern: /\blmao\b/i, animation: 'laugh' },
  { pattern: /\brofl\b/i, animation: 'laugh' },
  { pattern: /\bheh+\b/i, animation: 'smile' },
  { pattern: /\bhmm+\b/i, animation: 'thinking' },
];

export function detectEmojiAnimation(text: string): EmojiAnimation | null {
  for (const [emoji, animKey] of Object.entries(EMOJI_TO_ANIMATION)) {
    if (text.includes(emoji)) {
      return EMOJI_ANIMATIONS[animKey] ?? null;
    }
  }

  for (const trigger of TEXT_TRIGGERS) {
    if (trigger.pattern.test(text)) {
      return EMOJI_ANIMATIONS[trigger.animation] ?? null;
    }
  }

  return null;
}

let activeAnimFrame: number | null = null;

/**
 * Play an emoji animation on the avatar's morph targets
 */
export function playEmojiAnimation(
  animation: EmojiAnimation,
  morphMesh: THREE.SkinnedMesh,
  morphDict: Record<string, number>,
): void {
  if (activeAnimFrame !== null) {
    cancelAnimationFrame(activeAnimFrame);
    activeAnimFrame = null;
  }

  const resolved: Array<[number, number]> = [];
  for (const [key, val] of Object.entries(animation.morphTargets)) {
    const idx = resolveMorphTargetIndex(morphDict, key);
    if (idx !== undefined) resolved.push([idx, val]);
  }
  if (resolved.length === 0) return;

  const influences = morphMesh.morphTargetInfluences;
  if (!influences) return;

  const start = performance.now();
  const { duration, easeIn, easeOut } = animation;

  const animate = (now: number) => {
    const elapsed = now - start;
    if (elapsed > duration) {
      // Reset to zero
      for (const [idx] of resolved) {
        if (idx < influences.length) influences[idx] = Math.max(0, influences[idx] - 0.05);
      }
      activeAnimFrame = null;
      return;
    }

    let t: number;
    if (elapsed < easeIn) {
      t = elapsed / easeIn;
    } else if (elapsed > duration - easeOut) {
      t = (duration - elapsed) / easeOut;
    } else {
      t = 1.0;
    }
    t = Math.max(0, Math.min(1, t));

    for (const [idx, target] of resolved) {
      if (idx < influences.length) {
        influences[idx] = target * t;
      }
    }

    activeAnimFrame = requestAnimationFrame(animate);
  };

  activeAnimFrame = requestAnimationFrame(animate);
}
